import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, Clock, IndianRupee, Loader2, Plus, ShieldAlert, Users } from "lucide-react";
import { api, apiError, inr, logError, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const Field = ({ label, children }) => (
  <label className="block text-xs text-muted-foreground">
    {label}
    {children}
  </label>
);

function Stat({ label, value, testId }) {
  return (
    <div className="kpi-card" data-testid={testId}>
      <div className="overline">{label}</div>
      <div className="mt-2 font-mono-num text-2xl font-bold">{value}</div>
    </div>
  );
}

function EmployeeDialog({ emp, canSalary, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [profile, setProfile] = useState({});
  const [salary, setSalary] = useState({});
  const [shifts, setShifts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/hr/employees/${emp.id}`).then(({ data }) => {
      setDetail(data);
      setProfile({ employment_type: "Permanent", ot_eligible: true, incentive_eligible: true,
        leave_quota: { "Casual Leave": 12, "Sick Leave": 6, "Earned Leave": 12 }, ...data.hr });
      setSalary(data.hr?.salary || { basic: 0, hra: 0, conveyance: 0, special_allowance: 0,
        pf_applicable: false, esi_applicable: false, professional_tax: 0, tds_monthly: 0 });
    }).catch((e) => logError("hr employee", e));
    api.get("/hr/shifts").then(({ data }) => setShifts(data)).catch((e) => logError("shifts", e));
  }, [emp.id]);

  const save = async () => {
    setBusy(true);
    try {
      const { salary: _drop, salary_history: _h, id: _i, user_id: _u, ...clean } = profile;
      await api.put(`/hr/employees/${emp.id}/profile`, clean);
      if (canSalary) await api.put(`/hr/employees/${emp.id}/salary`, salary);
      toast.success("Employee record saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file, key) => {
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/files?entity=hr&entity_id=${emp.id}`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      if (key === "photo") setProfile((p) => ({ ...p, photo_file_id: data.id }));
      else setProfile((p) => ({ ...p, documents: [...(p.documents || []),
        { id: data.id, name: data.filename }] }));
      toast.success("Uploaded");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const setSal = (k, v) => setSalary((s) => ({ ...s, [k]: v }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="hr-employee-dialog">
        <DialogHeader>
          <DialogTitle>{emp.name} — {emp.employee_code || "—"}</DialogTitle>
        </DialogHeader>
        {!detail ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <Tabs defaultValue="master">
            <TabsList>
              <TabsTrigger value="master" data-testid="hr-tab-master">Master</TabsTrigger>
              {canSalary && <TabsTrigger value="salary" data-testid="hr-tab-salary">Salary</TabsTrigger>}
              <TabsTrigger value="history" data-testid="hr-tab-history">History</TabsTrigger>
            </TabsList>

            <TabsContent value="master" className="grid gap-3 sm:grid-cols-2">
              <Field label="Joining date">
                <Input type="date" data-testid="hr-joining-input" value={profile.joining_date || ""}
                  onChange={(e) => set("joining_date", e.target.value)} />
              </Field>
              <Field label="Employment type">
                <Select value={profile.employment_type} onValueChange={(v) => set("employment_type", v)}>
                  <SelectTrigger data-testid="hr-employment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Permanent", "Probation", "Contract", "Daily Wage", "Intern"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Work location">
                <Input data-testid="hr-location-input" value={profile.work_location || ""}
                  onChange={(e) => set("work_location", e.target.value)} />
              </Field>
              <Field label="Shift">
                <Select value={profile.shift_id || ""} onValueChange={(v) => set("shift_id", v)}>
                  <SelectTrigger data-testid="hr-shift-select"><SelectValue placeholder="Default shift" /></SelectTrigger>
                  <SelectContent>
                    {shifts.filter((s) => s.id).map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name} ({s.start}–{s.end})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Bank name">
                <Input data-testid="hr-bank-input" value={profile.bank_name || ""}
                  onChange={(e) => set("bank_name", e.target.value)} />
              </Field>
              <Field label="Account number">
                <Input data-testid="hr-account-input" value={profile.bank_account || ""}
                  onChange={(e) => set("bank_account", e.target.value)} />
              </Field>
              <Field label="IFSC">
                <Input data-testid="hr-ifsc-input" value={profile.bank_ifsc || ""}
                  onChange={(e) => set("bank_ifsc", e.target.value)} />
              </Field>
              <Field label="PAN">
                <Input data-testid="hr-pan-input" value={profile.pan || ""}
                  onChange={(e) => set("pan", e.target.value)} />
              </Field>
              <Field label="Emergency contact name">
                <Input data-testid="hr-emergency-name-input" value={profile.emergency_name || ""}
                  onChange={(e) => set("emergency_name", e.target.value)} />
              </Field>
              <Field label="Emergency contact number">
                <Input data-testid="hr-emergency-mobile-input" value={profile.emergency_mobile || ""}
                  onChange={(e) => set("emergency_mobile", e.target.value)} />
              </Field>
              <Field label="Registered photo (used for face check)">
                <Input type="file" accept="image/*" data-testid="hr-photo-input"
                  onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "photo")} />
              </Field>
              <Field label="KYC / documents">
                <Input type="file" data-testid="hr-doc-input"
                  onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "doc")} />
              </Field>
              <div className="flex items-center gap-4 sm:col-span-2">
                <label className="flex items-center gap-2 text-xs">
                  <Switch data-testid="hr-ot-switch" checked={profile.ot_eligible !== false}
                    onCheckedChange={(v) => set("ot_eligible", v)} /> Overtime eligible
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <Switch data-testid="hr-incentive-switch" checked={profile.incentive_eligible !== false}
                    onCheckedChange={(v) => set("incentive_eligible", v)} /> Incentive eligible
                </label>
                <span className="text-xs text-muted-foreground">
                  {(profile.documents || []).length} document(s) on file
                </span>
              </div>
              <Textarea className="sm:col-span-2" placeholder="Notes" data-testid="hr-notes-input"
                value={profile.notes || ""} onChange={(e) => set("notes", e.target.value)} />
            </TabsContent>

            {canSalary && (
              <TabsContent value="salary" className="grid gap-3 sm:grid-cols-2">
                {[["basic", "Basic"], ["hra", "HRA"], ["conveyance", "Conveyance"],
                  ["special_allowance", "Special allowance"], ["professional_tax", "Professional tax"],
                  ["tds_monthly", "TDS per month"]].map(([k, label]) => (
                  <Field key={k} label={label}>
                    <Input type="number" data-testid={`salary-${k}-input`} value={salary[k] ?? 0}
                      onChange={(e) => setSal(k, Number(e.target.value))} />
                  </Field>
                ))}
                <label className="flex items-center gap-2 text-xs">
                  <Switch data-testid="salary-pf-switch" checked={Boolean(salary.pf_applicable)}
                    onCheckedChange={(v) => setSal("pf_applicable", v)} /> PF applicable (12%)
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <Switch data-testid="salary-esi-switch" checked={Boolean(salary.esi_applicable)}
                    onCheckedChange={(v) => setSal("esi_applicable", v)} /> ESI applicable
                </label>
                <Input className="sm:col-span-2" placeholder="Revision note"
                  data-testid="salary-note-input" value={salary.revision_note || ""}
                  onChange={(e) => setSal("revision_note", e.target.value)} />
                <div className="sm:col-span-2 rounded-lg bg-secondary p-3 text-sm" data-testid="salary-gross">
                  Monthly gross:{" "}
                  <b className="font-mono-num">
                    {inr((Number(salary.basic) || 0) + (Number(salary.hra) || 0)
                      + (Number(salary.conveyance) || 0) + (Number(salary.special_allowance) || 0))}
                  </b>
                </div>
              </TabsContent>
            )}

            <TabsContent value="history" className="space-y-3 text-sm">
              <div data-testid="hr-history-payslips">
                <div className="overline mb-1">Payroll history</div>
                {(detail.payslips || []).length === 0 ? <p className="text-muted-foreground">No payslips yet.</p>
                  : detail.payslips.map((p) => (
                    <div key={p.id} className="flex justify-between border-b border-border py-1">
                      <span>{p.month}</span>
                      <span className="font-mono-num">{inr(p.line?.net_pay)}</span>
                    </div>
                  ))}
              </div>
              <div data-testid="hr-history-advances">
                <div className="overline mb-1">Advances</div>
                {(detail.advances || []).length === 0 ? <p className="text-muted-foreground">None.</p>
                  : detail.advances.map((a) => (
                    <div key={a.id} className="flex justify-between border-b border-border py-1">
                      <span>{a.reason || "Advance"} • {a.status}</span>
                      <span className="font-mono-num">{inr(a.balance)} left</span>
                    </div>
                  ))}
              </div>
              <div data-testid="hr-history-attendance">
                <div className="overline mb-1">This month attendance</div>
                {(detail.attendance_this_month || []).map((a) => (
                  <div key={a.id} className="flex justify-between border-b border-border py-1 text-xs">
                    <span>{a.date} • {a.status}</span>
                    <span className="text-muted-foreground">
                      {a.worked_hours}h {a.ot_hours ? `• OT ${a.ot_hours}h` : ""}
                      {(a.flags || []).length ? ` • ${a.flags[0]}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
        <DialogFooter>
          <Button data-testid="hr-save-button" onClick={save} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function HR() {
  const { can } = useAuth();
  const [summary, setSummary] = useState({});
  const [employees, setEmployees] = useState([]);
  const [canSalary, setCanSalary] = useState(false);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [incentives, setIncentives] = useState([]);
  const [open, setOpen] = useState(null);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [newAdvance, setNewAdvance] = useState({ user_id: "", amount: "", monthly_deduction: "", reason: "" });
  const [newIncentive, setNewIncentive] = useState({ user_id: "", kind: "Performance Bonus", amount: "", note: "" });
  const [reportFilter, setReportFilter] = useState({
    month: new Date().toISOString().slice(0, 7), department: "", search: "",
  });

  const load = useCallback(() => {
    api.get("/hr/summary").then(({ data }) => setSummary(data)).catch((e) => logError("hr summary", e));
    api.get("/hr/employees").then(({ data }) => {
      setEmployees(data.employees);
      setCanSalary(data.can_see_salary);
    }).catch((e) => logError("hr employees", e));
    api.get("/hr/attendance").then(({ data }) => setAttendance(data)).catch((e) => logError("attendance", e));
    api.get("/hr/leave").then(({ data }) => setLeaves(data.items)).catch((e) => logError("leave", e));
    api.get("/hr/shifts").then(({ data }) => setShifts(data)).catch((e) => logError("shifts", e));
    api.get("/hr/advances").then(({ data }) => setAdvances(data)).catch((e) => logError("advances", e));
    api.get("/hr/incentives").then(({ data }) => setIncentives(data.items)).catch((e) => logError("incentives", e));
  }, []);

  useEffect(() => { load(); }, [load]);

  const shownAttendance = useMemo(
    () => (flaggedOnly ? attendance.filter((a) => (a.flags || []).length) : attendance).slice(0, 120),
    [attendance, flaggedOnly]
  );

  const act = async (fn) => {
    try {
      await fn();
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      <PageHeader testId="hr-header" title="HR & Attendance"
        subtitle="Employee master, attendance with face and location checks, leave, advances and incentives." />

      <div className="grid grid-cols-2 gap-3 px-4 py-4 sm:grid-cols-3 sm:px-6 lg:grid-cols-6">
        <Stat label="Employees" value={summary.total_employees ?? 0} testId="hr-stat-employees" />
        <Stat label="Present today" value={summary.present_today ?? 0} testId="hr-stat-present" />
        <Stat label="Absent today" value={summary.absent_today ?? 0} testId="hr-stat-absent" />
        <Stat label="Late today" value={summary.late_today ?? 0} testId="hr-stat-late" />
        <Stat label="OT hours (month)" value={summary.overtime_hours_month ?? 0} testId="hr-stat-ot" />
        <Stat label="Needs review" value={summary.flagged_unreviewed ?? 0} testId="hr-stat-flagged" />
      </div>

      <Tabs defaultValue="team" className="px-4 pb-8 sm:px-6">
        <TabsList>
          <TabsTrigger value="team" data-testid="hr-tab-team"><Users className="mr-1.5 h-3.5 w-3.5" /> Team</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="hr-tab-attendance"><Clock className="mr-1.5 h-3.5 w-3.5" /> Attendance</TabsTrigger>
          <TabsTrigger value="leave" data-testid="hr-tab-leave"><CalendarDays className="mr-1.5 h-3.5 w-3.5" /> Leave</TabsTrigger>
          <TabsTrigger value="shifts" data-testid="hr-tab-shifts">Shifts</TabsTrigger>
          <TabsTrigger value="money" data-testid="hr-tab-money"><IndianRupee className="mr-1.5 h-3.5 w-3.5" /> Advances & Incentives</TabsTrigger>
          <TabsTrigger value="reports" data-testid="hr-tab-reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="reports" data-testid="hr-reports">
          <div className="mb-3 flex flex-wrap items-end gap-2">
            <Field label="Month">
              <Input type="month" className="w-40" data-testid="report-month-input"
                value={reportFilter.month} onChange={(e) => setReportFilter({ ...reportFilter, month: e.target.value })} />
            </Field>
            <Field label="Department">
              <Input className="w-40" placeholder="All departments" data-testid="report-department-input"
                value={reportFilter.department}
                onChange={(e) => setReportFilter({ ...reportFilter, department: e.target.value })} />
            </Field>
            <Field label="Employee name contains">
              <Input className="w-44" data-testid="report-search-input" value={reportFilter.search}
                onChange={(e) => setReportFilter({ ...reportFilter, search: e.target.value })} />
            </Field>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {[["employees", "Employee report"], ["attendance", "Attendance report"],
              ["leave", "Leave report"], ["overtime", "Overtime report"],
              ["salary", "Salary report"], ["payroll", "Payroll report"],
              ["department", "Department cost report"], ["incentives", "Incentive report"],
              ["advances", "Advance report"], ["anomalies", "AI anomaly report"]].map(([kind, label]) => (
              <a key={kind} data-testid={`report-download-${kind}`}
                href={`${process.env.REACT_APP_BACKEND_URL}/api/hr/reports/${kind}.csv?month=${
                  reportFilter.month}&department=${encodeURIComponent(reportFilter.department)}&search=${
                  encodeURIComponent(reportFilter.search)}`}
                className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2.5 text-sm transition-colors hover:bg-secondary">
                {label}
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">CSV</span>
              </a>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Salary, payroll and department cost exports need the salary permission. Every export is
            written to the audit log.
          </p>
        </TabsContent>

        <TabsContent value="team" data-testid="hr-team-list">
          {employees.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-3"
              data-testid={`hr-emp-row-${e.id}`}>
              <div className="min-w-0">
                <div className="font-medium">{e.name} <span className="text-xs text-muted-foreground">{e.employee_code}</span></div>
                <div className="text-xs text-muted-foreground">
                  {e.designation || e.role} • {e.department || "—"} • joined {shortDate(e.hr?.joining_date) || "—"}
                  {canSalary && e.hr?.salary?.gross ? ` • ${inr(e.hr.salary.gross)}/month` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!e.is_active && <Badge variant="destructive">inactive</Badge>}
                {!e.hr?.joining_date && <Badge variant="outline">HR pending</Badge>}
                <Button size="sm" variant="outline" data-testid={`hr-open-${e.id}`} onClick={() => setOpen(e)}>
                  Open record
                </Button>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="attendance" data-testid="hr-attendance-list">
          <Button size="sm" variant={flaggedOnly ? "default" : "outline"} className="mb-3 gap-1.5"
            data-testid="hr-flagged-toggle" onClick={() => setFlaggedOnly((v) => !v)}>
            <ShieldAlert className="h-3.5 w-3.5" /> Needs review only
          </Button>
          {shownAttendance.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-2.5"
              data-testid={`att-row-${a.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium">{a.user_name} • {a.date}</div>
                <div className="text-xs text-muted-foreground">
                  in {String(a.check_in || "").slice(11, 16)}
                  {a.check_out ? ` • out ${String(a.check_out).slice(11, 16)}` : " • no checkout"}
                  {` • ${a.worked_hours || 0}h`}{a.ot_hours ? ` • OT ${a.ot_hours}h` : ""}
                  {` • face ${a.face_status || "—"}`}{a.gps ? " • located" : " • no GPS"}
                  {a.sync_state === "offline_pending_sync" ? " • offline pending sync" : ""}
                </div>
                {(a.flags || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {a.flags.map((f) => <Badge key={f} variant="destructive" className="text-[9px]">{f}</Badge>)}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{a.status}</Badge>
                {(a.flags || []).length > 0 && !a.reviewed && can("attendance", "edit") && (
                  <Button size="sm" variant="outline" data-testid={`att-review-${a.id}`}
                    onClick={() => act(() => api.post(`/hr/attendance/${a.id}/review`,
                      { status: a.status, note: "Reviewed by HR" }))}>
                    Mark reviewed
                  </Button>
                )}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="leave" data-testid="hr-leave-list">
          {leaves.map((l) => (
            <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-2.5"
              data-testid={`leave-row-${l.id}`}>
              <div>
                <div className="text-sm font-medium">{l.user_name} • {l.leave_type}</div>
                <div className="text-xs text-muted-foreground">
                  {l.from_date} → {l.to_date} • {l.days} day(s) • {l.paid ? "paid" : "unpaid"}
                  {l.reason ? ` • ${l.reason}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={l.status === "Approved" ? "outline" : l.status === "Rejected" ? "destructive" : "default"}
                  data-testid={`leave-status-${l.id}`}>{l.status}</Badge>
                {l.status === "Pending" && can("leave", "approve") && (
                  <>
                    <Button size="sm" data-testid={`leave-approve-${l.id}`}
                      onClick={() => act(() => api.post(`/hr/leave/${l.id}/decide`, { status: "Approved" }))}>
                      Approve
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`leave-reject-${l.id}`}
                      onClick={() => act(() => api.post(`/hr/leave/${l.id}/decide`, { status: "Rejected" }))}>
                      Reject
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="shifts" data-testid="hr-shifts-list">
          <Button size="sm" className="mb-3 gap-1.5" data-testid="shift-add-button"
            onClick={() => act(() => api.post("/hr/shifts", {
              name: `Shift ${shifts.length + 1}`, type: "Normal", start: "09:30", end: "18:30",
              grace_minutes: 15, break_minutes: 45, hours: 8, ot_after_hours: 8.5,
              ot_multiplier: 1.5, holiday_ot_multiplier: 2, weekly_off: ["Sunday"],
              is_default: shifts.length === 0,
            }))}>
            <Plus className="h-3.5 w-3.5" /> Add shift
          </Button>
          {shifts.map((s) => (
            <div key={s.id || s.name} className="border-b border-border py-2.5" data-testid={`shift-row-${s.id || "default"}`}>
              <div className="text-sm font-medium">{s.name} {s.is_default && <Badge variant="outline">default</Badge>}</div>
              <div className="text-xs text-muted-foreground">
                {s.start}–{s.end} • {s.hours}h • grace {s.grace_minutes}m • break {s.break_minutes}m •
                OT after {s.ot_after_hours}h at {s.ot_multiplier}x (holiday {s.holiday_ot_multiplier || 2}x) •
                off {(s.weekly_off || []).join(", ")}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="money" className="space-y-6" data-testid="hr-money-tab">
          <div>
            <div className="overline mb-2">New advance / loan</div>
            <div className="flex flex-wrap items-end gap-2">
              <Select value={newAdvance.user_id} onValueChange={(v) => setNewAdvance({ ...newAdvance, user_id: v })}>
                <SelectTrigger className="w-52" data-testid="advance-employee-select">
                  <SelectValue placeholder="Employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" placeholder="Amount" className="w-32" data-testid="advance-amount-input"
                value={newAdvance.amount} onChange={(e) => setNewAdvance({ ...newAdvance, amount: e.target.value })} />
              <Input type="number" placeholder="Monthly cut" className="w-32" data-testid="advance-emi-input"
                value={newAdvance.monthly_deduction}
                onChange={(e) => setNewAdvance({ ...newAdvance, monthly_deduction: e.target.value })} />
              <Input placeholder="Reason" className="w-44" data-testid="advance-reason-input"
                value={newAdvance.reason} onChange={(e) => setNewAdvance({ ...newAdvance, reason: e.target.value })} />
              <Button size="sm" data-testid="advance-save-button" onClick={() => act(async () => {
                await api.post("/hr/advances", { ...newAdvance, amount: Number(newAdvance.amount),
                  monthly_deduction: Number(newAdvance.monthly_deduction) });
                setNewAdvance({ user_id: "", amount: "", monthly_deduction: "", reason: "" });
                toast.success("Advance recorded");
              })}>Record</Button>
            </div>
            {advances.map((a) => (
              <div key={a.id} className="flex items-center justify-between border-b border-border py-2 text-sm"
                data-testid={`advance-row-${a.id}`}>
                <span>{a.user_name} • {inr(a.amount)} • {inr(a.monthly_deduction)}/month • {a.reason || "—"}</span>
                <span className="flex items-center gap-2">
                  <span className="font-mono-num text-xs">{inr(a.balance)} left</span>
                  <Badge variant="outline" data-testid={`advance-status-${a.id}`}>{a.status}</Badge>
                  {a.status === "Pending" && (
                    <Button size="sm" data-testid={`advance-approve-${a.id}`}
                      onClick={() => act(() => api.post(`/hr/advances/${a.id}/approve`))}>Approve</Button>
                  )}
                </span>
              </div>
            ))}
          </div>

          <div>
            <div className="overline mb-2">Incentive / bonus</div>
            <div className="flex flex-wrap items-end gap-2">
              <Select value={newIncentive.user_id} onValueChange={(v) => setNewIncentive({ ...newIncentive, user_id: v })}>
                <SelectTrigger className="w-52" data-testid="incentive-employee-select">
                  <SelectValue placeholder="Employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={newIncentive.kind} onValueChange={(v) => setNewIncentive({ ...newIncentive, kind: v })}>
                <SelectTrigger className="w-44" data-testid="incentive-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Sales Incentive", "Performance Bonus", "Attendance Bonus", "Target Bonus",
                    "Festival Bonus", "Special Reward", "Custom"].map((k) => (
                    <SelectItem key={k} value={k}>{k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="number" placeholder="Amount" className="w-32" data-testid="incentive-amount-input"
                value={newIncentive.amount} onChange={(e) => setNewIncentive({ ...newIncentive, amount: e.target.value })} />
              <Button size="sm" data-testid="incentive-save-button" onClick={() => act(async () => {
                await api.post("/hr/incentives", { ...newIncentive, amount: Number(newIncentive.amount) });
                setNewIncentive({ user_id: "", kind: "Performance Bonus", amount: "", note: "" });
                toast.success("Incentive recorded");
              })}>Record</Button>
            </div>
            {incentives.map((i) => (
              <div key={i.id} className="flex items-center justify-between border-b border-border py-2 text-sm"
                data-testid={`incentive-row-${i.id}`}>
                <span>{i.user_name} • {i.kind} • {i.month}</span>
                <span className="flex items-center gap-2">
                  <span className="font-mono-num text-xs">{inr(i.amount)}</span>
                  <Badge variant="outline" data-testid={`incentive-status-${i.id}`}>{i.status}</Badge>
                  {i.status === "Pending" && (
                    <Button size="sm" data-testid={`incentive-approve-${i.id}`}
                      onClick={() => act(() => api.post(`/hr/incentives/${i.id}/approve`))}>Approve</Button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {open && (
        <EmployeeDialog emp={open} canSalary={canSalary} onClose={() => setOpen(null)} onSaved={load} />
      )}
    </div>
  );
}
